import { Box } from "@mui/material";
import Header from "../../components/Header";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRecentHistories,
  getRecentHistoryError,
  getRecentHistoryStatus,
  selectRecentHistory,
  selectRecentsLastRestaurantId,
  fetchStatistics,
  selectStatistics,
  getStatisticsStatus,
  getStatisticsError,
  selectStatisticsLastParams,
} from "../../features/historySlice";
import Error from "../../components/Error";
import { useEffect, useLayoutEffect, useState } from "react";
import Loading from "../../components/loading";
import { debounce } from "lodash";
import { ResponsiveProvider, useResponsive } from "../../hooks/uiHook";
import {
  getSettings,
  selectAllSettings,
  selectSettingsLastRestaurantId,
} from "../../features/settingSlice";
import { useRestaurant } from "../../hooks/RestaurantContext";
import FirstRow from "./firstRow";
import SecondRow from "./secondRow";
import { DaysFilterList } from "../../models/statistic.model";
import MenuItemFilter from "../../components/filters/menuItemFilter";
import DatepickerFilter from "../../components/filters/datepickerFilter";
import { useTheme } from "@emotion/react";
import dayjs from "dayjs";
import PieChart from "../../components/pieChart";
import { tokens } from "../../theme";
import LineChart from "../../components/lineChart";
import { useTranslation } from "react-i18next";
import { ChartCard } from "../../components/dashboardCard";
import PieChartIcon from "@mui/icons-material/PieChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { formatStatusText } from "../../models/status.model";

const STATUS_COLORS = {
  enCours: "#42a5f5",
  terminee: "#49BE25",
  annulee: "#ffac47",
  echouee: "#E9313C",
  remboursee: "#ec1d23",
  enRetard: "#f0444a",
  enAttente: "#ffac47",
};

const Dashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isLightMode = theme.palette.mode === "light";
  const startDateTime = new Date();
  const endDateTime = new Date();
  startDateTime.setHours(0, 0, 0, 0);
  endDateTime.setHours(23, 59, 59, 999);
  const { restaurantName, restaurantId } = useRestaurant();
  const { isSmallScreen } = useResponsive();
  const [filter, setFilter] = useState("today");
  const [dateRange, setDateRange] = useState({
    startDate: startDateTime,
    endDate: endDateTime,
  });
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const settings = useSelector(selectAllSettings);
  const settingsLastRestaurantId = useSelector(selectSettingsLastRestaurantId);
  const statistics = useSelector(selectStatistics);
  const statisticsStatus = useSelector(getStatisticsStatus);
  const statisticsError = useSelector(getStatisticsError);
  const statisticsLastParams = useSelector(selectStatisticsLastParams);
  const recents = useSelector(selectRecentHistory);
  const recentsError = useSelector(getRecentHistoryError);
  const recentsStatus = useSelector(getRecentHistoryStatus);
  const recentsLastRestaurantId = useSelector(selectRecentsLastRestaurantId);

  useEffect(() => {
    const statsParams =
      filter === "custom" && dateRange.startDate && dateRange.endDate
        ? {
            restaurantId,
            filter,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }
        : { restaurantId, filter };
    const serializedStatsParams = JSON.stringify(statsParams);
    if (
      !(
        statisticsLastParams === serializedStatsParams &&
        statisticsStatus === "succeeded"
      )
    ) {
      dispatch(fetchStatistics(statsParams));
    }

    if (!(settingsLastRestaurantId === restaurantId && settings)) {
      dispatch(getSettings(restaurantId));
    }

    if (
      !(
        recentsLastRestaurantId === restaurantId &&
        recentsStatus === "fetchRecentsData"
      )
    ) {
      dispatch(fetchRecentHistories(restaurantId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, restaurantId, filter, dateRange.startDate, dateRange.endDate]);

  useLayoutEffect(() => {
    const handleResize = debounce(() => {
      setWindowSize(window.innerWidth);
    }, 300);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [windowSize]);

  const isLoading =
    statisticsStatus === "loading" ||
    recentsStatus === "loading" ||
    !statistics ||
    !settings;
  const loadError =
    statisticsStatus === "failed"
      ? statisticsError
      : recentsStatus === "fetchRecentsError"
        ? recentsError
        : null;

  const orderStatusData = statistics?.orderStatuses
    ? Object.entries(statistics.orderStatuses)
        .filter(([, value]) => value > 0)
        .map(([key, value]) => ({
          id: key,
          label: t(`status.${key}`, { defaultValue: formatStatusText(key) }),
          value,
          color: STATUS_COLORS[key] || `hsl(${(value * 40) % 360}, 70%, 50%)`,
        }))
    : [];

  return (
    <ResponsiveProvider maxWidth={1150}>
      <Box m="20px" className="main-application">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          flexDirection={{ xs: "column", md: "row" }}
          gap={2}
          sx={{ mb: 3 }}
        >
          <Header
            title={t("dashboard.title")}
            subtitle={t("dashboard.welcome", {
              name: restaurantName || "",
            })}
          />
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: "12px",
              backgroundColor: colors.primary[400],
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <MenuItemFilter
              label={t("filters.filterBy")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              filters={DaysFilterList}
              isDark={isLightMode}
              noEmpt={true}
            />
            {filter === "custom" && (
              <>
                <DatepickerFilter
                  label={t("filters.startDate")}
                  value={dateRange.startDate}
                  onChange={(newValue) =>
                    setDateRange({ ...dateRange, startDate: newValue })
                  }
                  isDark={isLightMode}
                  maxDate={
                    dateRange.endDate &&
                    dayjs(dateRange.endDate).isBefore(dayjs())
                      ? dayjs(dateRange.endDate)
                      : dayjs()
                  }
                />
                <DatepickerFilter
                  label={t("filters.endDate")}
                  value={dateRange.endDate}
                  onChange={(newValue) =>
                    setDateRange({ ...dateRange, endDate: newValue })
                  }
                  isDark={isLightMode}
                  minDate={dateRange.startDate ? dayjs(dateRange.startDate) : null}
                  maxDate={dayjs()}
                />
              </>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Loading />
        ) : loadError ? (
          <Error>{loadError}</Error>
        ) : (
          <Box
            display="grid"
            gridTemplateColumns={isSmallScreen ? "1fr" : "repeat(12, 1fr)"}
            gridAutoRows="148px"
            gap="16px"
          >
            <FirstRow statistics={statistics} filter={filter} />
            <ChartCard
              gridColumn={isSmallScreen ? "1 / -1" : "span 4"}
              gridRow="span 2"
              title={t("dashboard.orderStatus")}
              icon={<PieChartIcon color="primary" />}
            >
              <PieChart data={orderStatusData} />
            </ChartCard>
            <SecondRow recents={recents} />
            <ChartCard
              gridColumn={isSmallScreen ? "1 / -1" : "span 8"}
              gridRow="span 2"
              title={t("dashboard.revenueOverTime")}
              icon={<ShowChartIcon color="primary" />}
            >
              <LineChart data={statistics.revenueOverTime ?? []} />
            </ChartCard>
          </Box>
        )}
      </Box>
    </ResponsiveProvider>
  );
};

export default Dashboard;
